import { isSelectableStudyAudienceStatus } from '@/lib/audience-selectability'

import { resolveAudienceCanonicalCoverUrl } from '@digital-twins/shared-types'
import type { AudiencePresentationSource } from '@digital-twins/shared-types'
export { resolveAudiencePersistedCoverUrl, resolveAudienceCanonicalCoverUrl } from '@digital-twins/shared-types'

type SetupPickerAudienceSource = AudiencePresentationSource & {
  status?: string | null
}

/**
 * Audiences a study can be fielded on, with a cover. `ready` and
 * `relaunching` match the study builder; relaunching still has to confirm
 * before it is selected. Failed, processing, and missing-cover rows stay out.
 */
export function isSetupPickerAudience(
  source: SetupPickerAudienceSource | null | undefined,
): boolean {
  if (!source || !isSelectableStudyAudienceStatus(source.status)) {
    return false
  }
  return Boolean(resolveAudienceCanonicalCoverUrl(source))
}

export function resolveAudienceTitleFont(
  source: AudiencePresentationSource,
) {
  const titleFont = source?.stageMetadata?.titleFont
  return typeof titleFont === 'string' && titleFont.trim().length > 0
    ? titleFont.trim()
    : 'Oswald'
}

type AudienceTwinCountSource = {
  status?: string | null
  rowCount?: number | null
  personaCount?: number | null
}

/**
 * Count to show as "digital twins": declared runtime row count when available,
 * with persona count only as a fallback for legacy/missing row-count payloads.
 */
export function resolveAudienceDigitalTwinCount(
  source: AudienceTwinCountSource | null | undefined,
): number | null {
  if (!source) return null
  const rc = source.rowCount
  const pc = source.personaCount
  if (typeof rc === 'number') return rc
  if (typeof pc === 'number') return pc
  return null
}

export function buildAudiencePresentationSignature(
  source: AudiencePresentationSource & {
    id?: string
    status?: string
    sharingMode?: string
    aiDescription?: string | null
    aiTags?: string[]
    rowCount?: number | null
    personaCount?: number | null
  },
) {
  const resolvedCoverUrl = resolveAudienceCanonicalCoverUrl(source) ?? ''
  const titleFont = resolveAudienceTitleFont(source) ?? ''
  const selectedCoverIndex =
    typeof source?.stageMetadata?.selectedCoverPhotoIndex === 'number'
      ? source.stageMetadata.selectedCoverPhotoIndex
      : ''

  return [
    source?.id ?? '',
    source?.status ?? '',
    source?.sharingMode ?? 'shared',
    resolvedCoverUrl,
    titleFont,
    selectedCoverIndex,
    source?.aiDescription ? '1' : '0',
    (source?.aiTags ?? []).length,
    source?.rowCount ?? '',
    source?.personaCount ?? '',
  ].join('|')
}
